import { CodeISO2, Country, countries } from "@mindlercare/schema";
import { useSelect } from "downshift";
import styled from "styled-components";
import { Icon } from "../icons";
import { Flag } from "../flag";

export type MarketDropdownProps = {
  label?: string;
  required?: boolean;
  disabled?: boolean;
  market: CodeISO2;
  onSelectedItemChange: (value: { selectedItem: Country | null }) => void;
};

const items = Object.values(countries);

function itemToString(item: Country | null) {
  return item ? item.name : "";
}

export const MarketDropdown = ({
  label,
  required,
  market,
  onSelectedItemChange,
  disabled = false,
}: MarketDropdownProps) => {
  const {
    getLabelProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    selectedItem,
    isOpen,
  } = useSelect<Country>({
    items,
    itemToString,
    selectedItem: items.find((item) => item.codeISO2 === market) || null,
    initialSelectedItem: items.find((item) => item.codeISO2 === market),
    onSelectedItemChange,
  });

  return (
    <div>
      <Container>
        <Label {...getLabelProps()}>
          {label}
          {required ? "*" : ""}
        </Label>
        <Input {...getToggleButtonProps()} $disabled={disabled}>
          <Flag market={selectedItem?.codeISO2} />
          <span>{selectedItem ? selectedItem.name : "Select market"}</span>
          <StyledArrowDown $disabled={disabled} />
        </Input>
        <ItemsPanel {...getMenuProps()}>
          {isOpen &&
            items.map((item, index) => (
              <Item key={item.countryId} {...getItemProps({ item, index })}>
                <span>{item.name}</span>
              </Item>
            ))}
        </ItemsPanel>
      </Container>
    </div>
  );
};

const Container = styled.div`
  position: relative;
`;

const Label = styled.label`
  font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
  font-size: ${({ theme }) => theme.FONT.SIZE.EXTRA_SMALL};
  font-weight: ${({ theme }) => theme.FONT.WEIGHT.REGULAR};
  color: ${({ theme }) => theme.COLORS.GRAY_DARK};
`;

const StyledArrowDown = styled(Icon.Expand)<{ $disabled: boolean }>`
  fill: ${({ theme, $disabled }) =>
    $disabled ? theme.COLORS.GRAY : theme.COLORS.BLACK};
`;

const Input = styled.div<{ $disabled: boolean }>`
  height: 48px;
  width: 304px;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  user-select: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  gap: 16px;
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
  border-radius: 4px;
  padding: 12px 16px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  margin-top: 4px;
  font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
  font-size: ${({ theme }) => theme.FONT.SIZE.STANDARD};
  font-weight: ${({ theme }) => theme.FONT.WEIGHT.REGULAR};

  & span {
    flex: 1;
    color: ${({ theme }) => theme.COLORS.GRAY};
  }
`;

const ItemsPanel = styled.ul`
  background-color: ${({ theme }) => theme.COLORS.WHITE};
  width: 304px;
  position: absolute;
  left: 0;
  z-index: 1000;
  padding: 0;
  margin: 0;
  list-style: none;
  box-shadow: 0px 2px 8px rgba(29, 27, 27, 0.2);
`;

const Item = styled.li`
  font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
  font-size: ${({ theme }) => theme.FONT.SIZE.SMALL};
  font-weight: ${({ theme }) => theme.FONT.WEIGHT.REGULAR};
  padding: 8px 16px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.COLORS.BLACK_GREY_OPACITY};
  }
`;
